// Helpers for cleaning clipboard HTML before it is inserted into the editor.

const BLOCK_SEL =
  'p,div,h1,h2,h3,h4,h5,h6,li,ul,ol,tr,td,th,table,thead,tbody,tfoot,blockquote,pre'

const KEEP_EMPTY = 'img,table,iframe,video'

// A <td> can't hold block elements without the browser splitting the cell into
// extra lines, so pasted blocks are dropped when empty and unwrapped to inline
// content (separated by <br>) when not. Inline formatting is preserved.
const hasContent = (n: ChildNode): boolean =>
  n.nodeType === 1
    ? !!(n as HTMLElement).textContent?.trim() || !!(n as HTMLElement).querySelector(KEEP_EMPTY)
    : !!n.textContent?.trim()

// Whether anything with actual content follows. Pretty-printed HTML puts
// whitespace text nodes between tags, and treating those as real siblings adds
// a <br> after the last block — a blank row inside the cell.
const followedByContent = (node: ChildNode): boolean => {
  for (let n = node.nextSibling; n; n = n.nextSibling) if (hasContent(n)) return true
  return false
}

const isBlankEdge = (n: ChildNode | null): boolean =>
  !!n && ((n.nodeType === 1 && (n as HTMLElement).tagName === 'BR') || !hasContent(n))

export function flattenToInline(root: HTMLElement): string {
  root.querySelectorAll(BLOCK_SEL).forEach((el) => {
    if (!el.textContent?.trim() && !el.querySelector(KEEP_EMPTY)) el.remove()
  })

  let block = root.querySelector(BLOCK_SEL)
  while (block) {
    const frag = document.createDocumentFragment()
    while (block.firstChild) frag.appendChild(block.firstChild)
    if (frag.childNodes.length && followedByContent(block)) {
      frag.appendChild(document.createElement('br'))
    }
    block.replaceWith(frag)
    block = root.querySelector(BLOCK_SEL)
  }

  // Whatever the source did, a <br> or blank node at either edge renders as an
  // empty row in the cell. This is the guarantee the ticket actually asks for.
  while (isBlankEdge(root.firstChild)) root.firstChild?.remove()
  while (isBlankEdge(root.lastChild)) root.lastChild?.remove()

  return root.innerHTML
}

// Clipboard HTML almost always arrives padded with empty <p>/<div> wrappers;
// pasting those verbatim adds blank lines above and below the pasted content.
export function trimBlankEdges(container: HTMLElement): string {
  const blank = (n: ChildNode | null) => {
    if (!n || n.textContent?.trim()) return false
    const el = n as HTMLElement
    return n.nodeType !== 1 || !(el.matches(KEEP_EMPTY) || el.querySelector(KEEP_EMPTY))
  }
  while (blank(container.firstChild)) container.firstChild?.remove()
  while (blank(container.lastChild)) container.lastChild?.remove()
  return container.innerHTML
}
